import { FC, useMemo } from 'react';
import { RiskDetailsCategory } from '@/components';
import { RiskDetails } from '@/models';
import { getRiskGroups, getRiskScoreValue, isFlowLimited } from '@/helpers';
import { SideEnum } from '@/api';
import { AmlHistoryItemData } from '@/redux/models';
import { useTranslation } from 'react-i18next';
import { RISK_GROUPS, RiskKeys } from '@/config';
import bluredDanger from '@/assets/images/blured-danger.svg';
import styles from './RiskDetailsInfo.module.sass';

interface RiskDetailsInfoProps {
  item: AmlHistoryItemData;
}

const RiskDetailsInfo: FC<RiskDetailsInfoProps> = (props) => {
  const { item } = props;
  const groups = getRiskGroups(item.signals, item.flow);
  const { t } = useTranslation();
  const hasSignalValues = useMemo(() => {
    if (!item.signals) return false;

    const hasSignals =
      Object.values(item.signals).filter((val) => Number(val) > 0.01).length >
      0;
    const hasHighRisk = item?.hasHighRisk || false;

    return hasSignals || hasHighRisk;
  }, [item]);

  const signalTitle = useMemo(() => {
    if (hasSignalValues) {
      if (!!item?.tx) {
        return item.direction === SideEnum.DEPOSIT
          ? t('translation.promo_tpl_source_of_funds_str')
          : t('translation.tx_risk_withdrawal_str');
      }

      return t('translation.wallet_connection_title');
    }

    if (!!item?.tx) {
      if (!!item.signals) {
        const lowSignalsPresent = [
          ...RISK_GROUPS.suspicious,
          ...RISK_GROUPS.trusted,
        ].some((signal) => Object.hasOwn(item.signals, signal));

        if (!lowSignalsPresent) {
          return '';
        }
      }

      return item.direction === SideEnum.DEPOSIT
        ? t('translation.tx_deposit_connections_empty_msg')
        : t('translation.tx_withdrawal_connections_empty_msg');
    }

    return t('translation.str_addr_connections_empty_msg');
  }, [item, hasSignalValues, t]);

  const showBluredBlock = useMemo(() => {
    if (!isFlowLimited(item.flow)) {
      return false;
    }
    return item?.hasHighRisk || false;
  }, [item]);

  const sanctions = useMemo(() => {
    if (
      isFlowLimited(item.flow) &&
      item.signals &&
      item.signals[RiskKeys.SANCTIONS]
    ) {
      return [
        {
          label: RiskKeys.SANCTIONS,
          value: getRiskScoreValue(item.signals[RiskKeys.SANCTIONS]) ?? 0,
        } as RiskDetails,
      ];
    }
    return [];
  }, [item]);

  const showDetails = useMemo(() => {
    if (Object.keys(groups).length > 0) {
      return true;
    }

    if (sanctions.length > 0) {
      return true;
    }

    if (showBluredBlock) {
      return true;
    }
    return false;
  }, [sanctions, showBluredBlock, groups]);

  return (
    <>
      <div className={styles.details__title}>{signalTitle}</div>
      {showDetails && (
        <div className={styles.details__list}>
          {Object.keys(groups).map((category) => (
            <div key={category}>
              <RiskDetailsCategory
                category={category}
                risks={groups[category]}
              />
            </div>
          ))}
          {(sanctions.length > 0 || showBluredBlock) && (
            <>
              {sanctions.length > 0 && (
                <RiskDetailsCategory category="sanctions" risks={sanctions} />
              )}
              {showBluredBlock && (
                <RiskDetailsCategory category="danger" risks={[]}>
                  <img src={bluredDanger} alt="" />
                </RiskDetailsCategory>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default RiskDetailsInfo;
